/**
 * Admin Dashboard Overview
 * Platform-wide metrics and quick actions
 */

import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Activity,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw,
} from "lucide-react";
import Card from "../../components/common/Card";
import { useAllTenants } from "../../hooks/useTenant";
import { usePlatformLLMCosts } from "../../hooks/useLLMCosts";
import { useTheme } from "../../context/ThemeContext";

function MetricCard({ title, value, change, changeType, icon: Icon, color }) {
  const isPositive = changeType === "positive";
  const colorClasses = {
    purple:
      "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400",
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
    green:
      "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400",
    orange:
      "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400",
  };

  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">
            {value}
          </p>
          {change && (
            <div
              className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? "text-green-600" : "text-red-600"}`}
            >
              {isPositive ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
}

export default function AdminDashboard() {
  const { currency } = useTheme();
  // const { data: tenants = [], isLoading, error, refetch } = useAllTenants();
  // const { data: llmCosts } = usePlatformLLMCosts();

  const tenants = [
    {
      id: 1,
      name: "Hotel Paradise",
      plan: "Premium",
      status: "active",
      users_count: 24,
      trips_count: 120,
    },
    {
      id: 2,
      name: "Sunrise Resort",
      plan: "Enterprise",
      status: "active",
      users_count: 18,
      trips_count: 95,
    },
    {
      id: 3,
      name: "Ocean View",
      plan: "Basic",
      status: "pending",
      users_count: 8,
      trips_count: 32,
    },
    {
      id: 4,
      name: "Royal Stay",
      plan: "Premium",
      status: "active",
      users_count: 15,
      trips_count: 76,
    },
  ];

  const isLoading = false;
  const error = null;
  const refetch = () => {};

  const llmCosts = {
    total_cost_usd: 124.56,
    by_tenant: [
      { tenant_id: "Hotel Paradise", cost_usd: 52.4 },
      { tenant_id: "Sunrise Resort", cost_usd: 41.2 },
      { tenant_id: "Ocean View", cost_usd: 30.96 },
    ],
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          Loading dashboard...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Failed to Load Dashboard
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-md">
          {error.response?.data?.error?.message ||
            error.message ||
            "Unable to connect to the admin API."}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
          Make sure the backend is running:{" "}
          <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
            python main.py serve
          </code>
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <RefreshCw className="w-4 h-4 inline mr-2" /> Retry
        </button>
      </div>
    );
  }

  const activeTenants = tenants.filter((t) => t.status === "active").length;
  const totalUsers = tenants.reduce((sum, t) => sum + (t.users_count || 0), 0);
  const totalTrips = tenants.reduce((sum, t) => sum + (t.trips_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
          Platform Overview
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Welcome to ServiceHive Admin Console
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Active Tenants"
          value={activeTenants}
          change="+2 this month"
          changeType="positive"
          icon={Building2}
          color="purple"
        />
        <MetricCard
          title="Total Users"
          value={totalUsers}
          change="+15 this week"
          changeType="positive"
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="Monthly Revenue"
          value="₹4.2L"
          change="+12% vs last month"
          changeType="positive"
          icon={DollarSign}
          color="green"
        />
        <MetricCard
          title="Active Trips"
          value={totalTrips}
          change="+45 today"
          changeType="positive"
          icon={Activity}
          color="orange"
        />
      </div>

      {/* Platform LLM Costs */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-700 dark:text-gray-300">
            Platform LLM Costs
          </h3>
          <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="text-3xl font-bold text-gray-900 dark:text-white">
          {currency}
          {llmCosts?.total_cost_usd?.toFixed(2) || "0.00"}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          This month
        </p>
        <div className="mt-4 space-y-2">
          {(llmCosts?.by_tenant || []).slice(0, 3).map((t) => (
            <div key={t.tenant_id} className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                {t.tenant_id}
              </span>
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {currency}
                {t.cost_usd?.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent Tenants & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tenants */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            Recent Tenants
          </h3>
          <div className="space-y-3">
            {tenants.slice(0, 5).map((tenant) => (
              <div
                key={tenant.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">
                      {tenant.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {tenant.plan} plan
                    </p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    tenant.status === "active"
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                  }`}
                >
                  {tenant.status}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* System Status */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            System Status
          </h3>
          <div className="space-y-4">
            {[
              { name: "API Gateway", status: "operational", latency: "45ms" },
              {
                name: "Pricing Engine",
                status: "operational",
                latency: "120ms",
              },
              { name: "Database", status: "operational", latency: "12ms" },
              { name: "Queue Service", status: "operational", latency: "8ms" },
              { name: "Blob Storage", status: "operational", latency: "25ms" },
            ].map((service) => (
              <div
                key={service.name}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">
                    {service.name}
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {service.latency}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
